import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, Table, Badge, Form, Button, Row, Tab, Tabs } from "react-bootstrap";
import Header from "../layouts/Header";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { StatTile } from "./usage/shared";
import LoginHistoryTab from "./usage/LoginHistoryTab";
import DevicesTab from "./usage/DevicesTab";
import ActivityTab from "./usage/ActivityTab";

const API = process.env.REACT_APP_API;
const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');

const resolveUploadUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${UPLOAD_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};

// ค่าใน tb_teacher.t_side
const SIDE_LABEL = {
  islamic:  'ศาสนา',
  academic: 'สามัญ',
  both:     'ศาสนาและสามัญ',
  ap:       'อนุบาล/ประถม',
  officer:  'เจ้าหน้าที่',
};

const MINUTE = 60;
const HOUR   = 60 * MINUTE;
const DAY    = 24 * HOUR;
const WEEK   = 7 * DAY;
const MONTH  = 30 * DAY;
const YEAR   = 365 * DAY;

// แปลงจำนวนวินาทีเป็นข้อความ นาที/ชั่วโมง/วัน/สัปดาห์/เดือน/ปี
// เช่น 5 นาทีที่แล้ว, 1 ชั่วโมงที่แล้ว, 2 สัปดาห์ที่แล้ว, 1 เดือนที่แล้ว, 2 ปีที่แล้ว
const formatAgo = (seconds) => {
  if (seconds === null || seconds === undefined) return 'ยังไม่เคยเข้าระบบ';
  const s = Math.max(0, Number(seconds));
  if (s < MINUTE) return 'เมื่อสักครู่';
  if (s < HOUR)   return `${Math.floor(s / MINUTE)} นาทีที่แล้ว`;
  if (s < DAY)    return `${Math.floor(s / HOUR)} ชั่วโมงที่แล้ว`;
  if (s < WEEK)   return `${Math.floor(s / DAY)} วันที่แล้ว`;
  if (s < MONTH)  return `${Math.floor(s / WEEK)} สัปดาห์ที่แล้ว`;
  if (s < YEAR)   return `${Math.floor(s / MONTH)} เดือนที่แล้ว`;
  return `${Math.floor(s / YEAR)} ปีที่แล้ว`;
};

// สีของ badge ตามความสดของการเข้าระบบล่าสุด
const agoVariant = (seconds) => {
  if (seconds === null || seconds === undefined) return 'secondary';
  const s = Number(seconds);
  if (s < DAY)   return 'success';
  if (s < WEEK)  return 'info';
  if (s < MONTH) return 'warning';
  return 'danger';
};

const RANGE_FILTERS = [
  { key: 'all',   label: 'ทั้งหมด',           match: () => true },
  { key: 'today', label: 'ภายใน 24 ชั่วโมง',  match: (s) => s !== null && s < DAY },
  { key: 'week',  label: 'ภายใน 7 วัน',       match: (s) => s !== null && s < WEEK },
  { key: 'month', label: 'ภายใน 30 วัน',      match: (s) => s !== null && s < MONTH },
  { key: 'stale', label: 'เกิน 30 วัน',       match: (s) => s !== null && s >= MONTH },
  { key: 'never', label: 'ยังไม่เคยเข้าระบบ', match: (s) => s === null },
];

const secondsOf = (r) => (r.seconds_ago === null || r.seconds_ago === undefined ? null : Number(r.seconds_ago));

// แท็บแรก: เวลาเข้าระบบล่าสุดของบุคลากรแต่ละคน (อ่านจาก tb_teacher.t_last_login)
function StaffLastLoginTab() {
  const { setLoading } = useLoading();
  const [rows, setRows]     = useState([]);
  const [search, setSearch] = useState('');
  const [range, setRange]   = useState('all');
  const [activeOnly, setActiveOnly] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`${API}/admin/getLoginStats`)
      .then(res => setRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { load(); }, [load]);

  const scoped = useMemo(
    () => (activeOnly ? rows.filter(r => String(r.t_status) === '1') : rows),
    [rows, activeOnly]
  );

  const summary = useMemo(() => {
    const count = (fn) => scoped.filter(r => fn(secondsOf(r))).length;
    return {
      total: scoped.length,
      today: count(s => s !== null && s < DAY),
      week:  count(s => s !== null && s < WEEK),
      never: count(s => s === null),
    };
  }, [scoped]);

  const filtered = useMemo(() => {
    const matchRange = (RANGE_FILTERS.find(f => f.key === range) || RANGE_FILTERS[0]).match;
    const q = search.trim();
    return scoped
      .filter(r =>
        matchRange(secondsOf(r)) &&
        (!q || r.t_name?.includes(q) || r.t_idnumber?.includes(q) || r.positions?.includes(q))
      )
      // เรียงตามเข้าระบบล่าสุด: คนที่เพิ่งเข้าระบบอยู่บนสุด, ยังไม่เคยเข้าระบบอยู่ล่างสุด
      .sort((a, b) => {
        const sa = secondsOf(a);
        const sb = secondsOf(b);
        if (sa === null && sb === null) return (a.t_idnumber || '').localeCompare(b.t_idnumber || '');
        if (sa === null) return 1;
        if (sb === null) return -1;
        return sa - sb;
      });
  }, [scoped, range, search]);

  return (
    <React.Fragment>
      <Row className="g-2 g-lg-3 mb-3">
        <StatTile label="บุคลากรทั้งหมด"        value={summary.total} variant="dark" />
        <StatTile label="เข้าระบบใน 24 ชั่วโมง" value={summary.today} variant="success" />
        <StatTile label="เข้าระบบใน 7 วัน"      value={summary.week}  variant="info" />
        <StatTile label="ยังไม่เคยเข้าระบบ"     value={summary.never} variant="danger" />
      </Row>

      <Card className="card-one">
        <Card.Header className="d-flex flex-wrap align-items-center gap-2">
          <Form.Control
            type="search"
            placeholder="ค้นหาชื่อ / รหัสผู้ใช้ / ตำแหน่ง"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 280 }}
          />
          <Form.Select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            {RANGE_FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </Form.Select>
          <Form.Check
            type="switch"
            id="activeOnly"
            label="เฉพาะผู้ใช้ที่เปิดใช้งาน"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="ms-auto"
          />
          <Button size="sm" variant="outline-primary" onClick={load}>
            <i className="ri-refresh-line me-1"></i>รีเฟรช
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 44 }}></th>
                  <th style={{ width: 110 }}>รหัสผู้ใช้</th>
                  <th>ชื่อ - นามสกุล</th>
                  <th>ตำแหน่ง</th>
                  <th style={{ width: 130 }}>ฝ่าย</th>
                  <th style={{ width: 170 }}>เข้าระบบล่าสุด</th>
                  <th style={{ width: 140 }}>วัน-เวลา</th>
                  <th style={{ width: 100 }} className="text-end">จำนวนครั้ง</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-secondary py-4">ไม่พบข้อมูล</td></tr>
                ) : filtered.map(r => {
                  const secs = secondsOf(r);
                  const photo = resolveUploadUrl(r.t_photo);
                  return (
                    <tr key={r.t_id}>
                      <td className="align-middle">
                        {photo
                          ? <img src={photo} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                          : <span style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>
                              {r.t_name ? r.t_name.trim().charAt(0) : '?'}
                            </span>}
                      </td>
                      <td className="align-middle fw-semibold text-nowrap">{r.t_idnumber}</td>
                      <td className="align-middle">
                        {r.t_name}
                        {String(r.t_status) !== '1' && <Badge bg="secondary" className="ms-2">ปิดใช้</Badge>}
                      </td>
                      <td className="align-middle fs-sm">{r.positions || <span className="text-muted">—</span>}</td>
                      <td className="align-middle fs-sm text-nowrap">{SIDE_LABEL[r.t_side] || '—'}</td>
                      <td className="align-middle">
                        <Badge bg={agoVariant(secs)}>{formatAgo(secs)}</Badge>
                      </td>
                      <td className="align-middle text-nowrap fs-sm">
                        {r.last_login_text || <span className="text-muted">—</span>}
                      </td>
                      <td className="align-middle text-end">{r.t_login_count || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer className="text-secondary fs-sm">
          แสดง {filtered.length} จาก {scoped.length} รายการ
        </Card.Footer>
      </Card>
    </React.Fragment>
  );
}

export default function AdminUsageStats() {
  const [tab, setTab] = useState('staff');

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="mb-4">
          <h4 className="main-title mb-1">สถิติการใช้งาน</h4>
          <p className="text-secondary mb-0 fs-sm">
            การเข้าระบบ อุปกรณ์ที่ใช้ และปริมาณการใช้งานจริงของบุคลากร
          </p>
        </div>

        {/* mountOnEnter: ให้แต่ละแท็บโหลดข้อมูลของตัวเองเมื่อถูกเปิดเท่านั้น ไม่ยิงพร้อมกันทั้งหมด */}
        <Tabs id="usage-stats-tabs" activeKey={tab} onSelect={(k) => setTab(k)} className="mb-4" mountOnEnter>
          <Tab eventKey="staff" title="บุคลากร">
            <StaffLastLoginTab />
          </Tab>
          <Tab eventKey="history" title="ประวัติการเข้าระบบ">
            <LoginHistoryTab />
          </Tab>
          <Tab eventKey="devices" title="อุปกรณ์และเครือข่าย">
            <DevicesTab />
          </Tab>
          <Tab eventKey="activity" title="การใช้งานจริง">
            <ActivityTab />
          </Tab>
        </Tabs>

        <p className="text-secondary fs-xs mt-3 mb-0">
          <i className="ri-shield-user-line me-1"></i>
          ข้อมูลการเข้าระบบ (ที่อยู่ IP อุปกรณ์ และเวลาที่ใช้งาน) เป็นข้อมูลส่วนบุคคลตาม พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล
          ระบบเก็บไว้ตามระยะเวลาที่กำหนดใน <code>USAGE_RETENTION_DAYS</code> (ค่าเริ่มต้น 180 วัน) แล้วลบอัตโนมัติ
          และเปิดดูได้เฉพาะผู้ดูแลระบบเท่านั้น
        </p>
      </div>
    </React.Fragment>
  );
}
